import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createRepo } from 'lib/api/firebase';
import {
  getPatientImpactProfile,
  impactPassportIdExists
} from 'lib/api/firebase/apis/apis';
import { parseAPISafeJSON } from 'lib/api/firebase/parseAPIJSON';
import { TestRecord } from 'lib/api/impact/types';
import {
  Collection,
  Inventory,
  LastTestCompleted,
  Patient,
  PatientProfile,
  // ApiSubCollection,
  PatientSubcollection,
  TestType
} from 'lib/documents';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import { handleError, handlePending, IHandleError } from 'lib/utils/redux';
import { getName } from 'lib/utils/string/normalise';
import { z } from 'zod';

export interface ImPACTSlice extends IHandleError {
  verified: boolean;
  passportId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  birthdate?: string | null;
  email?: string | null;
  userLoaded?: boolean;
  nextTestToComplete?: TestType;
  organizationID?: number | null;
  isLoading: boolean;
}

export const initialState: ImPACTSlice = {
  verified: false,
  passportId: null,
  firstName: null,
  lastName: null,
  birthdate: null,
  email: null,
  userLoaded: false,
  nextTestToComplete: TestType.POST_INJURY_ONE,
  organizationID: null,
  isLoading: false,
  hasError: false
};

export const initialImpactSliceLoad = createAsyncThunk(
  'impactAPI/initialLoadThunk',
  async (args: { patientId: string; assessmentType: AssessmentType }) => {
    /** Prepare data repos **/
    const patientRepo = createRepo<Patient>(Collection.Patients);
    const patientProfileRepo = createRepo<PatientProfile>(
      Collection.PatientProfiles
    );

    // Fetch user ConcussionRx profile
    const patient = await patientRepo.find(args.patientId);
    const patientProfile = await patientProfileRepo.find(args.patientId);

    // Fetch the ImPACT profile
    const patientImpactProfile = await getPatientImpactProfile({
      patientId: args.patientId
    }).then(parseAPISafeJSON);
    const { verified, passportId, lastTestCompleted } =
      patientImpactProfile.data;

    return {
      verified,
      passportId,
      patient,
      patientProfile,
      lastTestCompleted,
      assessmentType: args.assessmentType
    };
  }
);

const impactSlice = createSlice({
  name: 'impactAPI',
  initialState,
  reducers: {
    startLoading: state => {
      state.isLoading = true;
    },
    stopLoading: state => {
      state.isLoading = false;
    }
  },
  extraReducers: builder => {
    builder.addCase(initialImpactSliceLoad.pending, handlePending);
    builder.addCase(initialImpactSliceLoad.rejected, handleError);
    builder.addCase(initialImpactSliceLoad.fulfilled, (state, { payload }) => {
      state.isLoading = false;

      // Find the patients first and last name from the display name
      const name = getName(payload.patient?.displayName);
      const email = payload.patient?.email;

      //  we need to extract birthday from patientProfile and maybe language(?) if needed
      const birthdate = payload.patientProfile?.birthDate?.format('MM/DD/YYYY');

      // this prepopulates the impactAPI with data so the user doesn't have to submit things that shouldn't change;
      state.firstName = name?.firstName;
      state.lastName = name?.lastName;
      state.email = email;
      state.birthdate = birthdate;

      state.verified = payload.verified;
      state.passportId = payload.passportId;
      // state.patient = patient;
      state.userLoaded = true;

      // get next testtype to complete for impactAPI
      state.nextTestToComplete = resolveNextImPACTAssessment(
        payload.lastTestCompleted,
        payload.assessmentType
      );
    });
  }
});

/**
 * TODO: Move this to the backend
 *
 * @param previousTest
 * @param currentTestType
 * @returns
 */
function resolveNextImPACTAssessment(
  previousTest: LastTestCompleted | undefined,
  currentTestType: AssessmentType
) {
  // Override the ImPACT test to baseline if the Patient is in a baseline assessment
  if (currentTestType === 'baseline') {
    return TestType.BASELINE;
  }

  switch (previousTest) {
    case LastTestCompleted.NONE:
      return TestType.POST_INJURY_ONE;
    case LastTestCompleted.POST_INJURY_ONE:
      return TestType.POST_INJURY_TWO;
    case LastTestCompleted.POST_INJURY_TWO:
      return TestType.POST_INJURY_THREE;
    case LastTestCompleted.POST_INJURY_THREE:
      return TestType.POST_INJURY_FOUR;
    case LastTestCompleted.POST_INJURY_FOUR:
      /**
       * NOTE: The legacy system always returned POST_INJURY_FOUR after 4 assessments
       * TODO: Investigate the required behaviour
       */
      return TestType.POST_INJURY_FOUR;
    default:
      throw new Error(
        `[resolveNextImPACTAssessment] Unmapped previous test type '${previousTest}'`
      );
  }
}

export const ImpactSliceReducer = impactSlice.reducer;
export default impactSlice.reducer;

/**
 * TODO: Refactor this type
 */
export type ImpactInventory = Inventory & { data: TestRecord };
export const createImpactInventoryRepo = (patientId: string) =>
  createRepo<ImpactInventory>(
    [
      // WARN: USING LEGACY PATIENTS COLLECTION FOR ASSESSMENT DATA
      Collection.PatientAssessments,
      patientId,
      PatientSubcollection.InventoriesImpact
    ].join('/')
  );

/**
 * Check if an ImPACT PassportID is valid
 *
 * TODO: This check should be done on the backend
 * @param passportId
 * @returns
 */
export async function passportIdExists(passportId: string): Promise<boolean> {
  return z
    .boolean()
    .parse(
      (await impactPassportIdExists({ passportId, locationCode: 'CA' })).data
    );
}
